import { type FC } from 'react';
import {
    differenceInCalendarDays,
    getMonth,
    set,
    format,
    getDate,
} from 'date-fns';

import { TimeSmall } from '@zoopla-eng/zds-components/icons';

import {
    timestampTextStyle,
    timestampTextContrastStyle,
} from './Timestamp.styles.css';
import { fontGroups } from '@zoopla-eng/zds-components/themes/font-groups';

export const DAY_OF_INGEST = 15;

export type TimestampProps = {
    /** The last estimate ingest date. */
    ingestedAt: string;
    /** If the color styles should be contrasted. */
    contrastColors?: boolean;
    /** If the countdown version of the text should be displayed. */
    displayCountdown?: boolean;
    /** If the time icon should be shown alongside the timestamp. */
    displayIcon?: boolean;
};

const getDaysSinceIngest = (ingestedAt: string) =>
    differenceInCalendarDays(Date.now(), new Date(ingestedAt));

const getCountdownText = (ingestedAt: string): string | null => {
    const daysSinceIngest = getDaysSinceIngest(ingestedAt);

    if (daysSinceIngest === 0) {
        return 'Last updated today';
    }

    if (daysSinceIngest === 1) {
        return 'Last updated yesterday';
    }

    if (daysSinceIngest > 1 && daysSinceIngest <= 7) {
        return `Last updated on ${format(new Date(ingestedAt), 'EEEE')}`;
    }

    if (daysSinceIngest > 7) {
        const currentDay = getDate(Date.now());
        const currentMonth = getMonth(Date.now());
        const month =
            currentDay > DAY_OF_INGEST ? currentMonth + 1 : currentMonth;
        const nextIngestDate = set(new Date(), {
            month,
            date: DAY_OF_INGEST,
        });
        const daysUntilIngest = Math.abs(
            differenceInCalendarDays(Date.now(), nextIngestDate)
        );

        return daysUntilIngest === 0
            ? 'Next updated later today'
            : `Next updated in ${daysUntilIngest} ${
                  daysUntilIngest !== 1 ? 'days' : 'day'
              }`;
    }

    return null;
};

const getTimestampText = (ingestedAt: string): string | null => {
    const daysSinceIngest = getDaysSinceIngest(ingestedAt);

    if (daysSinceIngest <= 90) {
        return daysSinceIngest === 0
            ? 'Last updated today'
            : `Last updated ${daysSinceIngest} ${
                  daysSinceIngest !== 1 ? 'days' : 'day'
              } ago`;
    }

    return null;
};

const Timestamp: FC<React.PropsWithChildren<TimestampProps>> = ({
    ingestedAt,
    contrastColors = false,
    displayCountdown = false,
    displayIcon = false,
}) => {
    const timestampText = displayCountdown
        ? getCountdownText(ingestedAt)
        : getTimestampText(ingestedAt);

    return timestampText ? (
        <>
            {displayIcon && (
                <TimeSmall
                    accessibilityType="decorative"
                    color={
                        contrastColors
                            ? 'graphicIconsContrast'
                            : 'graphicIconsDefault'
                    }
                />
            )}
            <p
                className={`${
                    contrastColors
                        ? timestampTextContrastStyle
                        : timestampTextStyle
                } ${fontGroups.ladle}`}
                data-testid="last-updated-timestamp"
            >
                {timestampText}
            </p>
        </>
    ) : null;
};

export default Timestamp;
